/**
 * @fileoverview Table Body component.
 * Child component of Table used to render the <tbody> and its contents.
 * @author Derek Seo (dseo@yext.com)
 */
// @ts-nocheck
goog.declareModuleId('yext.ui.components.table.tablebody');

import {Button, DeprecatedOldButton} from '/ui/components/button/button';
import {Link} from '/ui/components/link/link';
import {MenuController} from '/ui/components/menu/menucontroller';
import {SelectController} from '/ui/components/select/selectcontroller';
import {loadingBodyTRs} from '/ui/components/table/body/loadingbody';
import {selectCell} from '/ui/components/table/body/selectcell';
import * as BodyTypes from '/ui/components/table/body/types';
import {INTERNAL_COL_IDS} from '/ui/components/table/common';
import * as Types from '/ui/components/table/types';
import {logError} from '/ui/lib/errors';

import * as styles from '/ui/components/table/body/tablebody.module.scss';

export {CellLink} from '/ui/components/table/body/celllink';

/**
 * @param {!BodyTypes.TableBodyProps} props
 * @return {React.ReactElement}
 */
export function TableBody(props) {
  const {emptyContent, dataRows, columns, isLoading} = props;

  verifyDataRows(dataRows);

  if (dataRows.length === 0 && emptyContent && !isLoading) {
    return (
      <tbody>
        <tr>
          <td className={styles.cell} colSpan={columns.length}>
            <div className={styles.emptyContent}>
              {emptyContent}
            </div>
          </td>
        </tr>
      </tbody>
    );
  }

  const trElems = isLoading ? loadingBodyTRs(props) : dataRowTRs(props);
  return (
    <tbody aria-busy={isLoading}>
      {trElems}
    </tbody>
  );
}

/**
 * Log an error if any dataRows have duplicate IDs.
 * @param {!Array<!Types.Row>} dataRows
 */
function verifyDataRows(dataRows) {
  const seenRowIds = new Set();
  const duplicateIds = new Set();
  dataRows.forEach(row => {
    if (seenRowIds.size === seenRowIds.add(row.id).size) {
      duplicateIds.add(row.id);
    }
  });
  if (duplicateIds.size > 0) {
    logError(`<TableBody> Duplicate row ids not permitted: ${[...duplicateIds].join(' ')}`);
  }
}

/**
 * @param {!BodyTypes.TableBodyProps} props
 * @return {React.ReactNode}
 */
function dataRowTRs(props) {
  return props.dataRows.map(
    dataRow => <tr key={dataRow.id}>{tableRowCells(dataRow, props)}</tr>,
  );
}

/**
 *
 * @param {!Types.Row} dataRow
 * @param {!BodyTypes.TableBodyProps} tableBodyProps
 * @returns {React.ReactNode}
 */
function tableRowCells(dataRow, tableBodyProps) {
  const {columns, rowCallToAction} = tableBodyProps;
  return columns.map(col => {
    if (rowCallToAction && col.isCallToAction) {
      return callToActionCell(dataRow, rowCallToAction);
    }

    if (col.isSelect) {
      return selectCell(dataRow, tableBodyProps);
    }

    const content = <div className={styles.cellContents}>
      {dataRow[col.id]}
    </div>;

    if (col.rowHeader) {
      return (
        <th
          className={styles.cell}
          key={col.id}
          scope='row'
        >
          {content}
        </th>
      );
    }

    return (
      <td className={styles.cell} key={col.id}>
        {content}
      </td>
    );
  });
}

/**
 * Returns the contents for a cell in the callToAction column.
 * @param {!Types.Row} dataRow
 * @param {!Types.RowCallToAction | undefined} rowCallToAction
 * @returns {React.ReactNode}
 * @suppress {checkTypes}
 */
function callToActionCell(dataRow, rowCallToAction) {
  if (!rowCallToAction) {
    return undefined;
  }
  const callToAction = rowCallToAction(dataRow);

  // TODO (bteo): Remove the @suppress {checkTypes} annotation once better TS typings are
  // included (UX-1938). Right now React.ReactElement doesn't contain the type property.
  let isValidCTA = false;
  if (callToAction === null) {
    isValidCTA = true;
  } else if (callToAction.type === Button
    || callToAction.type === DeprecatedOldButton
    || callToAction.type === MenuController
    || callToAction.type === SelectController
    || callToAction.type === Link
  ) {
    isValidCTA = true;
  }

  if (!isValidCTA) {
    logError('<Table/>: rowCallToAction() should return either a Button, MenuController, Link or SelectController component, or null for no CTA in that row.');
  }

  if (callToAction && callToAction.type === MenuController
      && !callToAction.props.allowContainerOverflow) {
    logError('<Table/>: <MenuController/> returned by rowCallToAction() should set allowContainerOverflow prop to true');
  }

  if (callToAction && callToAction.type === SelectController
      && (!callToAction.props.config || !callToAction.props.config.allowContainerOverflow)) {
    logError('<Table/>: <SelectController/> returned by rowCallToAction() should set config.allowContainerOverflow prop to true.');
  }

  return (
    <td className={`${styles.cell} ${styles.callToActionCell}`} key={INTERNAL_COL_IDS.ROW_CTA}>
      <div className={`${styles.cellContents} ${styles.callToActionContents}`}>
        {callToAction}
      </div>
    </td>
  );
}
